// File: /opt/dev_environment/assets/workspace/app-init/src/pages/VerbenPage/VerbCardForm.js

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CONJUGATION_FORMS, PERSONS } from '../../constants/verbData';
import '../VokabelnPage/VokabelnPage.css';

function VerbCardForm({ title, initialData, onSave, onCancel }) {
  const isEdit = !!initialData;

  const [formData, setFormData] = useState({
    baseVerb: initialData?.baseVerb || '',
    germanInfinitive: initialData?.germanInfinitive || '',
    conjugationForm: initialData?.conjugationForm || '',
    targetPerson: initialData?.targetPerson || '',
    conjugatedAnswer: initialData?.conjugatedAnswer || '',
    germanConjugation: initialData?.germanConjugation || '',
  });

  const verbInputRef = useRef(null);

  useEffect(() => {
    if (verbInputRef.current && !isEdit) {
      verbInputRef.current.focus();
    }
  }, [isEdit]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const selectedForm = CONJUGATION_FORMS.find((f) => f.value === formData.conjugationForm);
  const isParticipio = formData.conjugationForm === 'participioPassato';

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!formData.conjugatedAnswer.trim()) return;

    if (isEdit) {
      const selectedPerson = PERSONS.find((p) => p.value === formData.targetPerson);
      onSave({
        targetPersonLabel: isParticipio ? 'Participio Passato' : (selectedPerson?.label || formData.targetPerson),
        targetFormDescription: isParticipio
          ? 'Participio Passato'
          : `${selectedPerson?.label || formData.targetPerson}, ${selectedForm?.label || formData.conjugationForm}`,
        conjugatedAnswer: formData.conjugatedAnswer.trim(),
        germanConjugation: formData.germanConjugation.trim(),
      });
    } else {
      if (!formData.baseVerb.trim() || !formData.conjugationForm) return;
      const selectedPerson = PERSONS.find((p) => p.value === formData.targetPerson);
      onSave({
        type: 'verb',
        baseVerb: formData.baseVerb.trim(),
        germanInfinitive: formData.germanInfinitive.trim(),
        verbGroup: 'custom',
        conjugationForm: formData.conjugationForm,
        conjugationFormLabel: selectedForm?.label || formData.conjugationForm,
        conjugationFormGermanLabel: selectedForm?.germanLabel || '',
        targetPerson: isParticipio ? null : formData.targetPerson,
        targetPersonLabel: isParticipio ? 'Participio Passato' : (selectedPerson?.label || ''),
        targetPersonGermanLabel: isParticipio ? (selectedForm?.germanLabel || '') : (selectedPerson?.germanLabel || ''),
        targetFormDescription: isParticipio
          ? 'Participio Passato'
          : `${selectedPerson?.label || ''}, ${selectedForm?.label || ''}`,
        targetFormDescriptionGerman: isParticipio
          ? (selectedForm?.germanLabel || '')
          : `${selectedPerson?.germanLabel || ''}, ${selectedForm?.germanLabel || ''}`,
        conjugatedAnswer: formData.conjugatedAnswer.trim(),
        germanConjugation: formData.germanConjugation.trim(),
        createdAt: new Date().toISOString(),
      });
    }
  }, [formData, isEdit, isParticipio, selectedForm, onSave]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }, [onCancel]);

  const isValid = isEdit
    ? formData.conjugatedAnswer.trim() !== ''
    : formData.baseVerb.trim() !== '' && formData.conjugationForm !== '' && formData.conjugatedAnswer.trim() !== '';

  return (
    <div
      className="vokabeln-dialog-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="vokabeln-dialog">
        <div className="vokabeln-dialog__header">
          <h2 className="vokabeln-dialog__title">{title}</h2>
          <button
            className="vokabeln-dialog__close"
            type="button"
            onClick={onCancel}
            aria-label="Dialog schließen"
          >
            ✕
          </button>
        </div>

        <form className="vokabeln-dialog__body" onSubmit={handleSubmit}>
          {/* Base Verb */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="verb-base">
              Verb (Infinitiv) {isEdit ? '(schreibgeschützt)' : '*'}
            </label>
            <input
              ref={verbInputRef}
              id="verb-base"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.baseVerb}
              onChange={(e) => handleChange('baseVerb', e.target.value)}
              placeholder="z.B. dormire"
              required={!isEdit}
              readOnly={isEdit}
              autoComplete="off"
              style={isEdit ? { opacity: 0.6, backgroundColor: 'var(--color-progress-bg)' } : {}}
            />
          </div>

          {/* German Infinitive */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="verb-german-inf">
              Deutsche Bedeutung (Infinitiv) {isEdit ? '(schreibgeschützt)' : ''}
            </label>
            <input
              id="verb-german-inf"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.germanInfinitive}
              onChange={(e) => handleChange('germanInfinitive', e.target.value)}
              placeholder="z.B. schlafen"
              readOnly={isEdit}
              autoComplete="off"
              style={isEdit ? { opacity: 0.6, backgroundColor: 'var(--color-progress-bg)' } : {}}
            />
          </div>

          {/* Conjugation Form */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="verb-form">
              Konjugationsform {isEdit ? '(schreibgeschützt)' : '*'}
            </label>
            <select
              id="verb-form"
              className="vokabeln-form-field__select"
              value={formData.conjugationForm}
              onChange={(e) => handleChange('conjugationForm', e.target.value)}
              disabled={isEdit}
              style={isEdit ? { opacity: 0.6, backgroundColor: 'var(--color-progress-bg)' } : {}}
            >
              <option value="">— Form wählen —</option>
              {CONJUGATION_FORMS.map((f) => (
                <option key={f.value} value={f.value}>{f.label} ({f.germanLabel})</option>
              ))}
            </select>
          </div>

          {/* Target Person (not for participio passato) */}
          {formData.conjugationForm && !isParticipio && (
            <div className="vokabeln-form-field">
              <label className="vokabeln-form-field__label" htmlFor="verb-person">
                Zielperson
              </label>
              <select
                id="verb-person"
                className="vokabeln-form-field__select"
                value={formData.targetPerson}
                onChange={(e) => handleChange('targetPerson', e.target.value)}
              >
                <option value="">— Person wählen —</option>
                {PERSONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label} → {p.germanLabel}</option>
                ))}
              </select>
            </div>
          )}

          {/* Conjugated Form (Italian Answer) */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="verb-answer">
              Italienische Konjugation *
            </label>
            <input
              id="verb-answer"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.conjugatedAnswer}
              onChange={(e) => handleChange('conjugatedAnswer', e.target.value)}
              placeholder="z.B. dormo"
              required
              autoComplete="off"
            />
          </div>

          {/* German Conjugation */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="verb-german-conj">
              Deutsche Konjugation
            </label>
            <input
              id="verb-german-conj"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.germanConjugation}
              onChange={(e) => handleChange('germanConjugation', e.target.value)}
              placeholder="z.B. ich schlafe"
              autoComplete="off"
            />
          </div>

          {/* Actions */}
          <div className="vokabeln-dialog__actions">
            <button
              className="vokabeln-dialog__btn vokabeln-dialog__btn--cancel"
              type="button"
              onClick={onCancel}
            >
              Abbrechen
            </button>
            <button
              className="vokabeln-dialog__btn vokabeln-dialog__btn--save"
              type="submit"
              disabled={!isValid}
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VerbCardForm;